import { FileNameTypes } from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { SideDrawer } from '@/components/shared/SideDrawer';
import { useDragToScroll } from '@/hooks';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { DrawersProps } from '@/types';
import { Close } from '@mui/icons-material';
import { IconButton, Stack, Typography } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useMemo } from 'react';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { Image } from '@/components/Analysis/shared/Image';
import { getThumbnailStyles, MainImageContainer, ScrollContainer } from './styles';
import { LocationTag } from '@/constants';

export const SplitViewPanel = ({ allClosed, isOpen, handleOpen }: DrawersProps) => {
  const [selectedIsometricFileId, setSelectedIsometricFileId] = useAtom(
    state.selectedIsometricFileId
  );
  const assemblyDetails = useAtomValue(state.selectedAssemblyDetails);
  const setSideBySideModeEnabled = useSetAtom(state.sideBySideModeEnabled);

  const { rightDrawerWidth } = useDrawerWidths();
  const { containerReference, handleMouseDown } = useDragToScroll();

  const isometricFiles = useMemo(() => {
    return (assemblyDetails?.files ?? [])?.filter(
      (file) => file.name === FileNameTypes.IsometricDrawing
    );
  }, [assemblyDetails]);

  const selectedIsometricFile = useMemo(() => {
    return isometricFiles.find((file) => file.id === selectedIsometricFileId);
  }, [isometricFiles, selectedIsometricFileId]);

  const handleClose = () => {
    setSelectedIsometricFileId(undefined);
    setSideBySideModeEnabled(false);
  };

  if (!assemblyDetails || !selectedIsometricFileId) return <></>;

  return (
    <SideDrawer
      allDrawersClosed={allClosed}
      position="right"
      width={rightDrawerWidth}
      isOpen={isOpen}
      handleOpen={handleOpen}
      iconStyles={{ top: 10, zIndex: '102 !important' }}
      sxPaper={{ overflowY: 'hidden', border: 0 }}
    >
      <Stack padding="2.4rem" gap="4.8rem" id={LocationTag.ViewerSBSIsometricsPanel}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
          <Typography variant="h6" fontWeight={700} lineHeight="1.6rem">
            {selectedIsometricFile?.caption ?? selectedIsometricFile?.fileName ?? ''}
          </Typography>
          <IconButton
            disableRipple
            sx={{ padding: 0 }}
            onClick={handleClose}
            data-testid="close-button"
          >
            <Close color="primary" />
          </IconButton>
        </Stack>
        {selectedIsometricFile && (
          <MainImageContainer className={`${LocationTag.ViewerSBSIsometricsPanel}-main-image`}>
            <Image
              src={getCloudfrontUrl(selectedIsometricFile?.resourcePath)}
              alt={selectedIsometricFile?.name}
              maxHeight="45.7rem"
            />
          </MainImageContainer>
        )}
        <ScrollContainer ref={containerReference} onMouseDown={handleMouseDown}>
          <Stack direction="row" alignItems="center" gap="0.8rem">
            {isometricFiles.map((file) => (
              <Stack
                key={file.id}
                direction="column"
                alignItems="center"
                gap="0.4rem"
                data-testid={`thumbnail-container-${file.id}`}
                className={`${LocationTag.ViewerSBSIsometricsPanel}-thumbnail`}
              >
                <Image
                  src={getCloudfrontUrl(file?.resourcePath)}
                  alt={file?.name}
                  imgSx={{
                    height: '8rem',
                  }}
                  sx={getThumbnailStyles(selectedIsometricFileId, file)}
                  onClick={() => setSelectedIsometricFileId(file?.id ?? '')}
                  data-testid={`thumbnail-${file.id}`}
                />
                <Typography variant="caption" color="primary.400" fontWeight={500}>
                  {file?.caption ?? file?.fileName ?? ''}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </ScrollContainer>
      </Stack>
    </SideDrawer>
  );
};
